import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { AmpelBadge, SignalListe, Abschnitt, FazitBox, LeerHinweis, farben } from 'core/components/ui';
import { formatKompakt, formatZahl } from 'core/utils/format';

const achseStil = { fontSize: 11, fontFamily: 'JetBrains Mono, monospace', fill: farben.inkMuted };

/** Farbiger Bewertungs-Chip je Kategorie (gut/mittel/schwach/keine). */
function BewertungChip({ bewertung }) {
  const map = {
    gut: 'bg-gain/10 text-gain ring-gain/20',
    mittel: 'bg-[#9a7b1e]/10 text-[#9a7b1e] ring-[#9a7b1e]/20',
    schwach: 'bg-loss/10 text-loss ring-loss/20',
    keine: 'bg-ink/5 text-ink-muted ring-line',
  };
  const label = { gut: 'gut', mittel: 'mittel', schwach: 'schwach', keine: 'keine Daten' };
  return (
    <span className={`rounded-full px-2.5 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wide ring-1 ${map[bewertung] || map.keine}`}>
      {label[bewertung] || bewertung}
    </span>
  );
}

function KategorieKarte({ kategorie }) {
  return (
    <div className="rounded-xl border border-line bg-paper-soft p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="font-medium text-ink">{kategorie.name}</h4>
        <BewertungChip bewertung={kategorie.bewertung} />
      </div>
      {kategorie.text && <p className="mb-3 text-xs leading-relaxed text-ink-muted">{kategorie.text}</p>}
      <SignalListe signale={kategorie.kennzahlen} />
    </div>
  );
}

export default function FundamentalAnalyse({ fundamental, currency }) {
  if (!fundamental || !fundamental.basisJahr) {
    return (
      <LeerHinweis>
        {fundamental?.fazit || 'Für diese Aktie liegen keine Fundamentaldaten vor.'}
      </LeerHinweis>
    );
  }

  const reihe = fundamental.jahresReihe || [];
  const hatGuV = reihe.some((j) => j.umsatz != null || j.jahresueberschuss != null);
  const hatEps = reihe.some((j) => j.eps != null);

  return (
    <div>
      {/* Score + Ampel */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-paper-soft p-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-wider text-ink-muted">
            Fundamentales Gesamtbild · Basisjahr {fundamental.basisJahr}
          </div>
          <div className="mt-0.5 font-serif text-2xl font-semibold text-ink">
            {fundamental.score != null ? `${fundamental.score}/100` : '–'}
          </div>
        </div>
        <AmpelBadge ampel={fundamental.ampel} score={fundamental.score} size="lg" />
      </div>

      {/* Umsatz / Gewinn / Marge */}
      {hatGuV && (
        <Abschnitt titel="Umsatz, Gewinn & Nettomarge je Jahr">
          <div className="rounded-xl border border-line bg-paper-soft p-2">
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={reihe} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                <CartesianGrid stroke={farben.line} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="jahr" tick={achseStil} stroke={farben.line} />
                <YAxis
                  yAxisId="geld"
                  tick={achseStil}
                  width={56}
                  stroke={farben.line}
                  tickFormatter={(v) => formatKompakt(v, currency)}
                />
                <YAxis
                  yAxisId="proz"
                  orientation="right"
                  tick={achseStil}
                  width={44}
                  stroke={farben.line}
                  tickFormatter={(v) => `${formatZahl(v, 0)}%`}
                />
                <Tooltip
                  formatter={(v, name) =>
                    name === 'Nettomarge'
                      ? [`${formatZahl(v, 1)} %`, name]
                      : [formatKompakt(v, currency), name]
                  }
                />
                <Bar yAxisId="geld" dataKey="umsatz" name="Umsatz" fill={farben.brand} fillOpacity={0.25} maxBarSize={26} />
                <Bar yAxisId="geld" dataKey="jahresueberschuss" name="Jahresüberschuss" fill={farben.ink} fillOpacity={0.75} maxBarSize={26} />
                <Line yAxisId="proz" type="monotone" dataKey="nettomarge" name="Nettomarge" stroke={farben.gain} strokeWidth={1.6} dot={{ r: 2 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Abschnitt>
      )}

      {/* EPS */}
      {hatEps && (
        <Abschnitt titel="Gewinn je Aktie (EPS)">
          <div className="rounded-xl border border-line bg-paper-soft p-2">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={reihe} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                <CartesianGrid stroke={farben.line} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="jahr" tick={achseStil} stroke={farben.line} />
                <YAxis tick={achseStil} width={48} stroke={farben.line} tickFormatter={(v) => formatZahl(v, 1)} />
                <Tooltip formatter={(v) => [formatZahl(v, 2), 'EPS']} />
                <Bar dataKey="eps" name="EPS" fill={farben.ink} fillOpacity={0.7} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Abschnitt>
      )}

      {/* Kategorie-Scorecard */}
      <Abschnitt titel="Bewertung nach Kategorien">
        <div className="grid gap-4 md:grid-cols-2">
          {(fundamental.kategorien || []).map((k) => (
            <KategorieKarte key={k.name} kategorie={k} />
          ))}
        </div>
      </Abschnitt>

      <FazitBox>{fundamental.fazit}</FazitBox>
    </div>
  );
}
